import React from "react";
import {
  IconButton,
  Link,
  Stack,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import { useAuth } from "../../hooks/useAuth";
import ContentCopyIcon from "@mui/icons-material/ContentCopyRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import SettingsDialog from "./SettingsDialog";
import ConfirmationDialog from "./ConfirmationDialog";
import { fetchBotConfigRest, patchBotConfig } from "../../api/botConfig";
import { ReconnectingWebSocket } from "../../socket";
import { useResponsive } from "../../hooks/useResponsive";
import {
  formatPercent,
  formatWalletAddress,
  formatDateTime,
  formatUsdOrDash,
} from "../../utils/formatters";
import { BotConfig, IStats } from "../../shared/types";
import DetailItem from "../../components/cards/subcomponents/DetailItem";
import { SurfaceCard } from "../../components/ui/SurfaceCard";
import { SectionHeader } from "../../components/ui/SectionHeader";
import { StatusPill } from "../../components/ui/StatusPill";

/** Summary amounts: whole dollars from $1,000 up, cents below; zero reads "—". */
const usd = formatUsdOrDash;

/** Step names arrive in capitals ("DERIBIT"): show them in sentence case ("Deribit"). */
const sentenceCase = (step: string) =>
  /[a-z]/.test(step) ? step : step.charAt(0) + step.slice(1).toLowerCase().replace(/_/g, " ");

interface Props {
  stats: IStats;
  decimals: number;
  onCopy: (text: string, label: string) => void;
  botConfig?: BotConfig;
  onForceClose?: () => void;
  onCloseAndPause?: () => void;
  onPauseResume?: (enabled: boolean) => void;
  loadingAction?: boolean;
  loadingConfig?: boolean; // New prop
  onSetLoadingConfig: (isLoading: boolean) => void; // New prop
  onSaveConfig?: (updatedConfig: BotConfig) => void;
  socket: ReconnectingWebSocket; // Add socket prop
}

const CurrentPoolCard: React.FC<Props> = ({
  stats,
  decimals,
  onCopy,
  botConfig,
  onForceClose,
  onCloseAndPause,
  onPauseResume,
  loadingAction,
  loadingConfig, // Destructure loadingConfig
  onSetLoadingConfig, // Destructure onSetLoadingConfig
  onSaveConfig,
  socket,
}) => {
  const { isMobile } = useResponsive();
  const { groups } = useAuth();
  const isAdmin = groups?.includes("admin");

  function getExplorerLink(address: string): string {
    const name = stats.botName.toLowerCase();

    if (name.startsWith("arb")) {
      return `https://arbiscan.io/address/${address}`;
    }

    if (name.startsWith("rsk")) {
      return `https://rootstock.blockscout.com/address/${address}`;
    }

    return `https://etherscan.io/address/${address}`;
  }

  function getUniswapLink(address: string): string {
    const name = stats.botName.toLowerCase();

    if (name.startsWith("arb")) {
      return `https://app.uniswap.org/explore/pools/arbitrum/${address}`;
    }

    if (name.startsWith("rsk")) {
      return `https://oku.trade/uniswap/v3/liquidity/rootstock/${address}`;
    }

    return `https://app.uniswap.org/explore/pools/ethereum/${address}`;
  }

  function getPositionLink(
    botName: string,
    positionId: number,
    walletAddress: string
  ): string {
    const name = botName.toLowerCase();

    if (name.startsWith("rsk")) {
      return `https://oku.trade/info/rootstock/user/${walletAddress}`;
    }

    if (name.startsWith("arb")) {
      return `https://app.uniswap.org/positions/v3/arbitrum/${positionId}`;
    }

    // Default to ETH
    return `https://app.uniswap.org/positions/v3/ethereum/${positionId}`;
  }

  const positionLink = getPositionLink(
    stats.botName,
    stats.positionId,
    stats.walletAddress
  );

  // Fees of the OPEN position only: `currentPoolCollectedFees` is the latest position's, even after it
  // closed (the backend's `accruedFeesUSD` is the open position's uncollected fees, when sent).
  const collectedFeesDisplay = (() => {
    if (!stats.isPositionOpen) return "—";
    const fees = stats.accruedFeesUSD ?? stats.currentPoolCollectedFees;
    const total = stats.currentPoolAmount;
    if (fees && total > 0) {
      const pct = fees / total;
      return `${usd(fees)} (${formatPercent(pct)})`;
    }
    return usd(fees);
  })();

  const [openSettings, setOpenSettings] = React.useState(false);
  const [editConfig, setEditConfig] = React.useState<BotConfig | undefined>(
    undefined
  );
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [savingConfig, setSavingConfig] = React.useState(false);
  const [configSaveError, setConfigSaveError] = React.useState<string | null>(
    null
  );

  const normalizeConfigForSave = (config: BotConfig): BotConfig => {
    const mode = config.positionSize?.mode || "PERCENT";
    const normalized: BotConfig = {
      ...config,
      positionSize: { mode },
    };

    if (mode === "TOKEN_AMOUNTS") {
      normalized.positionSize = {
        mode,
        token0Amount: Math.max(0, Number(config.positionSize?.token0Amount) || 0),
        token1Amount: Math.max(0, Number(config.positionSize?.token1Amount) || 0),
      };
    }

    return normalized;
  };

  const handleOpenSettings = async () => {
    setOpenSettings(true);
    onSetLoadingConfig(true); // Set loading to true when fetching starts
    try {
      const fetchedConfig = await fetchBotConfigRest(stats.botName);
      setEditConfig(fetchedConfig);
    } catch (error) {
      console.error("Failed to fetch bot config:", error);
      // Optionally show an error message to the user
    } finally {
      onSetLoadingConfig(false); // Set loading to false when fetching finishes (success or error)
    }
  };

  // Determine if bot is active based on available data
  const isBotActive = (() => {
    // If we have real botConfig, use its enabled state
    if (botConfig) {
      return botConfig.enabled;
    }
    // If no botConfig but we have pool amount or position, assume bot is active
    if (stats.currentPoolAmount > 0 || stats.positionId > 0) {
      return true;
    }
    // Otherwise assume inactive
    return false;
  })();

  const handleFieldChange = (field: string, value: any) => {
    setEditConfig((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleNestedFieldChange = (
    parent: string,
    field: string,
    value: any
  ) => {
    setEditConfig((prev) =>
      prev
        ? {
            ...prev,
            [parent]: {
              ...((prev as any)[parent] || {}),
              [field]: value,
            },
          }
        : prev
    );
  };

  const handleDoubleNestedFieldChange = (
    parent: string,
    child: string,
    field: string,
    value: any
  ) => {
    setEditConfig((prev) =>
      prev
        ? {
            ...prev,
            [parent]: {
              ...((prev as any)[parent] || {}),
              [child]: {
                ...(((prev as any)[parent] || {})[child] || {}),
                [field]: value,
              },
            },
          }
        : prev
    );
  };

  const handleSave = () => {
    if (!editConfig) return;
    setConfigSaveError(null);

    const normalizedConfig = normalizeConfigForSave(editConfig);
    if (
      normalizedConfig.positionSize?.mode === "TOKEN_AMOUNTS" &&
      (normalizedConfig.positionSize.token0Amount || 0) === 0 &&
      (normalizedConfig.positionSize.token1Amount || 0) === 0
    ) {
      alert("Enter at least one token amount before saving.");
      return;
    }

    setEditConfig(normalizedConfig);
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!editConfig) return;
    setConfigSaveError(null);

    // Get password from user
    const password = window.prompt(
      "Enter password to confirm configuration changes:"
    );
    if (!password) {
      setShowConfirm(false);
      return;
    }

    setSavingConfig(true);

    try {
      const normalizedConfig = normalizeConfigForSave(editConfig);
      // Prepare the config data for saving (exclude the id field)
      const { id, ...configData } = normalizedConfig;

      // Call the API to save the configuration
      await patchBotConfig(socket, stats.botName, configData, password);

      // Update the parent component with the new config
      if (onSaveConfig) {
        onSaveConfig(normalizedConfig);
      }

      // Close modals and show success
      setShowConfirm(false);
      setOpenSettings(false);

      // You could add a success notification here if you have a notification system
    } catch (error) {
      console.error("Failed to save bot configuration:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to save configuration. Please check your password and try again.";
      setConfigSaveError(message);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleCancelSave = () => {
    setConfigSaveError(null);
    setShowConfirm(false);
  };

  const linkSep = (
    <Typography component="span" sx={{ color: "text.disabled", px: 0.25 }}>
      ·
    </Typography>
  );
  // The 23px copy button's 44px touch area reaches ~11px past its edge: 20px from the
  // preceding (explorer) link keeps the two targets from overlapping.
  const copyButton = (value: string, label: string) => (
    <Tooltip title={`Copy ${label.toLowerCase()}`}>
      <IconButton onClick={() => onCopy(value, label)} aria-label={`Copy ${label}`} sx={{ ml: 1.75 }}>
        <ContentCopyIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      <SurfaceCard sx={{ height: "100%" }}>
        <SectionHeader
          title="Current pool"
          icon={WaterDropOutlinedIcon}
          subtitle={stats.isPositionOpen ? `Position #${stats.positionId}` : "No open position"}
          action={
            isAdmin && (
              <Tooltip title="Bot settings">
                <IconButton
                  onClick={handleOpenSettings}
                  aria-label="Settings"
                  sx={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: botConfig ? "secondary.main" : "text.secondary",
                  }}
                >
                  <TuneRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )
          }
        />

        <Stack spacing={0.25}>
          <DetailItem
            label="Wallet"
            value={
              <>
                <Link
                  href={`https://debank.com/profile/${stats.walletAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  DeBank
                </Link>
                {linkSep}
                <Link
                  href={getExplorerLink(stats.walletAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                >
                  {formatWalletAddress(stats.walletAddress)}
                </Link>
                {copyButton(stats.walletAddress ?? "", "Wallet Address")}
              </>
            }
          />

          <DetailItem
            label="Pool"
            value={
              <>
                <Link
                  href={getExplorerLink(stats.poolAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Explorer
                </Link>
                {linkSep}
                <Link
                  href={getUniswapLink(stats.poolAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Uniswap
                </Link>
                {copyButton(stats.poolAddress ?? "", "Pool Address")}
              </>
            }
          />

          <DetailItem
            label="Position ID"
            value={
              <>
                <Link
                  href={`${positionLink}#user_positions`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    const baseUrl = positionLink;
                    const hashUrl = `${positionLink}#user_positions`;
                    const win = window.open(baseUrl, "_blank");
                    setTimeout(() => {
                      if (win && !win.closed) {
                        win.location.href = hashUrl;
                      }
                    }, 2000);
                  }}
                >
                  {stats.positionId}
                </Link>
                {copyButton(stats.positionId.toString(), "PositionID")}
              </>
            }
          />

          <Divider sx={{ my: 0.75 }} />

          <DetailItem
            label="Locked amount"
            value={
              stats.currentPoolAmount === 0 && !stats.isPositionOpen ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <StatusPill tone="brand" label="Balancing" size="sm" />
                  <Typography component="span" sx={{ fontSize: "inherit", fontWeight: 600 }}>
                    {usd(stats.currentPoolAmount)}
                  </Typography>
                </Stack>
              ) : (
                usd(stats.currentPoolAmount)
              )
            }
            emphasis
          />

          <DetailItem label="Position fees" value={collectedFeesDisplay} />

          {/* Last Updated timestamp */}
          {stats.positionLastUpdated && (
            <DetailItem
              label="Last updated"
              value={formatDateTime(stats.positionLastUpdated)}
            />
          )}

          {/* Admin: Show current balancing step */}
          {isAdmin && stats.currentBalancingStep && (
            <DetailItem
              label="Balancing step"
              value={
                <Typography component="span" noWrap sx={{ fontSize: "inherit", fontWeight: 600 }}>
                  {sentenceCase(stats.currentBalancingStep)}
                  {stats.currentBalancingSubStep && (
                    <Typography component="span" sx={{ fontSize: "0.75rem", color: "text.secondary", ml: 0.75 }}>
                      {stats.currentBalancingSubStep}
                    </Typography>
                  )}
                </Typography>
              }
            />
          )}
        </Stack>
      </SurfaceCard>

      <SettingsDialog
        open={openSettings}
        onClose={() => setOpenSettings(false)}
        isMobile={isMobile}
        isBotActive={isBotActive}
        loadingAction={loadingAction}
        onPauseResume={onPauseResume}
        onForceClose={onForceClose}
        onCloseAndPause={onCloseAndPause}
        editConfig={editConfig}
        token0Symbol={stats.token0Symbol}
        token1Symbol={stats.token1Symbol}
        walletToken0={stats.balanceSnapshotEnd?.walletToken0}
        walletToken1={stats.balanceSnapshotEnd?.walletToken1}
        handleFieldChange={handleFieldChange}
        handleNestedFieldChange={handleNestedFieldChange}
        handleDoubleNestedFieldChange={handleDoubleNestedFieldChange}
        handleSave={handleSave}
        savingConfig={savingConfig}
        loadingConfig={loadingConfig} // Pass loadingConfig prop
      />

      <ConfirmationDialog
        open={showConfirm}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        savingConfig={savingConfig}
        errorMessage={configSaveError}
      />
    </>
  );
};

export default CurrentPoolCard;
